import mixpanel, { type Dict } from "mixpanel-browser";

// Indirection so Cypress component tests can cy.stub these methods — ES module
// exports can't be stubbed directly (same pattern as SentryWrapper in lib/apollo.tsx).
export const MixpanelWrapper = {
  init: (token: string, config: Dict) => mixpanel.init(token, config),
  register: (props: Dict) => mixpanel.register(props),
  identify: (id: string) => mixpanel.identify(id),
  peopleSet: (props: Dict) => mixpanel.people.set(props),
  track: (name: string, props?: Dict) => mixpanel.track(name, props),
  reset: () => mixpanel.reset(),
  getDistinctId: (): string => mixpanel.get_distinct_id()
};

let initialized = false;

function appSurface(pathname: string): string {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/clients")) return "client";
  return "session";
}

// Drops null/undefined/"" values: Mixpanel guidance is to omit empty
// properties entirely rather than send placeholders.
export function compactProperties(props?: Dict): Dict | undefined {
  if (!props) return undefined;
  const out: Dict = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === "") continue;
    out[key] = value;
  }
  return out;
}

export function isAnalyticsEnabled(): boolean {
  return initialized;
}

export function initAnalytics(token?: string): void {
  if (initialized || typeof window === "undefined") return;
  const resolved = token ?? import.meta.env.VITE_MIXPANEL_TOKEN;
  if (!resolved) return;
  // Keep automated runs out of analytics; an explicitly passed token wins so
  // component tests can exercise init against the stubbed wrapper.
  if (token === undefined && (import.meta.env.MODE === "test" || window.Cypress)) return;

  try {
    MixpanelWrapper.init(resolved, {
      debug: import.meta.env.MODE === "development",
      // page_viewed is tracked manually (events.ts) for naming + reload-safe dedupe.
      track_pageview: false,
      persistence: "localStorage"
    });
    MixpanelWrapper.register({
      environment: import.meta.env.MODE,
      app_surface: appSurface(window.location.pathname)
    });
    initialized = true;
  } catch {
    // Analytics must never break the app.
  }
}

export interface AnalyticsUser {
  id: string | number
  email?: string | null
  full_name?: string | null
  role_name?: string | null
}

export function identifyUser(user: AnalyticsUser): void {
  if (!initialized) return;
  try {
    MixpanelWrapper.identify(String(user.id));
    MixpanelWrapper.peopleSet(compactProperties({
      $email: user.email,
      $name: user.full_name,
      role: user.role_name
    })!);
  } catch { }
}

// True when mixpanel currently holds a real user identity (simplified ID merge
// prefixes anonymous ids with "$device:").
export function hasIdentifiedUser(): boolean {
  if (!initialized) return false;
  try {
    return !MixpanelWrapper.getDistinctId().startsWith("$device:");
  } catch {
    return false;
  }
}

export function resetAnalytics(): void {
  if (!initialized) return;
  try {
    MixpanelWrapper.reset();
  } catch { }
}

export function track(name: string, props?: Dict): void {
  if (!initialized) return;
  try {
    MixpanelWrapper.track(name, compactProperties(props));
  } catch { }
}

// Test-only: clears module state so each spec can exercise init from scratch.
export function _resetAnalyticsStateForTests(): void {
  initialized = false;
}
